import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarBlank, MapPin, Clock, ArrowsClockwise, UserCircle, BookmarkSimple, SignOut, PlusCircle } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const Header = ({ onRefresh, refreshing, lastUpdated, onReturnHome }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true
    });
  };

  const formatDay = (date) => {
    return date.toLocaleDateString("en-US", { weekday: "long" });
  };

  const formatLastUpdated = (date) => {
    if (!date) return null;
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  return (
    <header 
      className="sticky top-0 z-50 glass-header px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4"
      data-testid="main-header"
    >
      <div className="max-w-[1800px] mx-auto flex items-center justify-between gap-2 sm:gap-4">
        {/* Logo & Title */}
        <div 
          className="flex items-center gap-2 sm:gap-3 flex-shrink-0 cursor-pointer group"
          onClick={onReturnHome}
          data-testid="home-logo-btn"
          title="Return to homepage"
        >
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-[#E07A5F] group-hover:bg-[#D46A50] flex items-center justify-center shadow-sm transition-colors">
            <MapPin weight="duotone" className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div>
            <h1 
              className="text-lg sm:text-xl md:text-2xl font-semibold tracking-tight text-[#2B2D42]"
              style={{ fontFamily: "'Work Sans', sans-serif" }}
              data-testid="app-title"
            >
              411 Fest
            </h1>
            <p className="text-xs text-[#6C757D] hidden sm:block">
              Find your next show across the US
            </p>
          </div>
        </div>

        {/* Center - Submit & Refresh Buttons */}
        <div className="flex items-center gap-2 sm:gap-3">
          <Button
            onClick={() => navigate("/submit-event")}
            variant="outline"
            className="border-[#81B29A] text-[#81B29A] hover:bg-[#81B29A]/10 rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium"
            data-testid="submit-event-nav-btn"
          >
            <PlusCircle weight="bold" className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Submit Event</span>
          </Button>
          <Button
            onClick={onRefresh}
            disabled={refreshing}
            className="bg-[#81B29A] hover:bg-[#6A9A83] text-white rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-all"
            data-testid="refresh-shows-btn"
          >
            <ArrowsClockwise 
              weight="bold" 
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''} sm:mr-2`} 
            />
            <span className="hidden sm:inline">{refreshing ? "Searching..." : "Find New Shows"}</span>
          </Button>
          {lastUpdated && (
            <span className="text-xs text-[#6C757D] hidden md:block">
              Updated: {formatLastUpdated(lastUpdated)}
            </span>
          )}
        </div>

        {/* Right side - Clock + Auth */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Live Clock Display */}
          <div 
            className="hidden sm:flex items-center gap-2 sm:gap-3 bg-white/60 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 border border-[#D6D3D1]/50 shadow-sm flex-shrink-0"
            data-testid="live-clock-display"
          >
            <CalendarBlank weight="duotone" className="w-4 h-4 sm:w-5 sm:h-5 text-[#E07A5F] flex-shrink-0" />
            <div className="flex flex-col">
              <span 
                className="text-xs sm:text-sm font-semibold text-[#2B2D42] leading-tight"
                data-testid="current-day"
              >
                {formatDay(currentTime)}, {currentTime.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                })}
              </span>
              <span 
                className="text-[10px] sm:text-xs text-[#6C757D] font-mono leading-tight"
                data-testid="current-time"
              >
                {formatTime(currentTime)}
              </span>
            </div>
          </div>

          {/* Auth - User Menu or Sign In */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full border border-[#D6D3D1]/50 bg-white/60 p-1 pr-2 sm:pr-3 hover:bg-white/80 transition-colors"
                  data-testid="user-menu-btn"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover" />
                  ) : (
                    <UserCircle weight="duotone" className="w-7 h-7 sm:w-8 sm:h-8 text-[#6C757D]" />
                  )}
                  <span className="hidden sm:block text-sm font-medium text-[#2B2D42] max-w-[100px] truncate">
                    {user.name?.split(" ")[0] || "Account"}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium text-[#2B2D42] truncate">{user.name}</p>
                  <p className="text-xs text-[#6C757D] truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/my-shows")} className="cursor-pointer" data-testid="my-shows-menu-item">
                  <BookmarkSimple weight="duotone" className="w-4 h-4 mr-2 text-[#E07A5F]" />
                  My Shows
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-500" data-testid="logout-menu-item">
                  <SignOut weight="bold" className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={login}
              variant="outline"
              className="border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F]/10 rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium"
              data-testid="sign-in-btn"
            >
              <UserCircle weight="duotone" className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Sign In</span>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
